import { OnboardingFormFields } from '@Types/index';
import { useCallback } from 'react';
import { getStateDropdownItem } from './AddressStateUtil';
import { FieldValues, Path, PathValue, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { ExtendedUseFormReturn } from '@Hooks/index';

interface IProps<T extends FieldValues> {
  state: string | undefined;
  line1: string | undefined;
  line2: string | undefined;
  city: string | undefined;
  postalCode: string | undefined;
  setValue: UseFormSetValue<T>;
  trigger: UseFormTrigger<T>;
  formMethods: ExtendedUseFormReturn<T>;
  defaultValues: T;
}

export const useHandleOnCheckedChange = <T extends FieldValues>({
  state,
  line1,
  line2,
  city,
  postalCode,
  setValue,
  trigger,
  formMethods,
  defaultValues: defValues,
}: IProps<T>) => {
  const handleOnCheckChange = useCallback(
    (isChecked: boolean) => {
      const currentState = getStateDropdownItem(state);

      const triggerValidationInAddress = () => {
        trigger(OnboardingFormFields.LINE1 as Path<T>);
        trigger(OnboardingFormFields.APARTMENT as Path<T>);
        trigger(OnboardingFormFields.CITY as Path<T>);
        trigger(OnboardingFormFields.STATE as Path<T>);
        trigger(OnboardingFormFields.ZIP as Path<T>);
      };

      if (isChecked) {
        setValue(OnboardingFormFields.STATE as Path<T>, currentState as PathValue<T, Path<T>>);
        setValue(OnboardingFormFields.LINE1 as Path<T>, line1 as PathValue<T, Path<T>>);
        setValue(OnboardingFormFields.CITY as Path<T>, city as PathValue<T, Path<T>>);
        setValue(OnboardingFormFields.ZIP as Path<T>, postalCode as PathValue<T, Path<T>>);
        setValue(OnboardingFormFields.APARTMENT as Path<T>, line2 as PathValue<T, Path<T>>);
        triggerValidationInAddress();
      } else {
        const defaultValues: Partial<Record<keyof typeof OnboardingFormFields, string>> = {
          [OnboardingFormFields.LINE1]: defValues[OnboardingFormFields.LINE1],
          [OnboardingFormFields.APARTMENT]: defValues[OnboardingFormFields.APARTMENT],
          [OnboardingFormFields.CITY]: defValues[OnboardingFormFields.CITY],
          [OnboardingFormFields.STATE]: defValues[OnboardingFormFields.STATE],
          [OnboardingFormFields.ZIP]: defValues[OnboardingFormFields.ZIP],
        };

        Object.keys(defaultValues).forEach((key) => {
          formMethods.resetField(key as Path<T>, {
            defaultValue: defaultValues[key as keyof typeof OnboardingFormFields] as PathValue<
              T,
              Path<T>
            >,
          });
        });
      }
    },
    [city, line1, line2, postalCode, setValue, state, trigger, formMethods, defValues]
  );
  return { handleOnCheckChange };
};
